<script setup lang="ts">
import { useResizeObserver } from "@vueuse/core";
import type { IHeaderParams } from "ag-grid-community";
import { computed, nextTick, onUnmounted, ref, watch } from "vue";
import { useRouter } from "vue-router";

import { getFilterDefinitionList } from "~/common/filter";
import { getPropertyConfig, getPropertyWithConfigList } from "~/common/properties";
import { ASCENDING, DESCENDING, NO_SORT, setSort, sortDirection } from "~/common/sort";
import DropdownMenu from "~/components/dumb/DropdownMenu.vue";
import FillOutWithAiItem from "~/components/dumb/FillOutWithAiItem.vue";
import SortIndicator from "~/components/filters/SortIndicator.vue";
import { notify } from "~/components/notifications";
import { NO_SETTINGS_PROPERTY_KIND_SET } from "~/constants/property";
import {
  AutomationsIcon,
  DuidFieldIcon,
  FilterIcon,
  HideIcon,
  OrderFieldIcon,
  SettingsIcon,
  SortAscendingIcon,
  SortDescendingIcon,
  UnsortAscendingIcon,
  UnsortDescendingIcon,
} from "~/icons";
import { makeLinkToAutomationSettingsRef, makeLinkToPropertySettingsRef } from "~/router/common";
import {
  DropdownMenuItemKind,
  EditorMode,
  FilterApplicability,
  FilterConnector,
  NotificationType,
  PropertyKind,
  SubtaskDisplayMode,
  UserRole,
  ViewKind,
} from "~/shared/enums";
import type { Property, TaskField, VisualizationItem } from "~/shared/types";
import { useAppStore, useDataStore, usePageStore, useTenantStore, useUserStore } from "~/stores";
import { calculateListLeftPadding, makeUuid } from "~/utils/common";
import { generateFieldRecommendation, getRecommendationDefaultValuesForProperties } from "~/utils/recommendation";

const COLUMN_ICON_MAP = new Map([
  ["duid", DuidFieldIcon],
  ["order", OrderFieldIcon],
]);

const props = defineProps<{
  params: IHeaderParams<VisualizationItem> & { property?: Property; editorMode?: EditorMode };
}>();

const router = useRouter();
const appStore = useAppStore();
const dataStore = useDataStore();
const pageStore = usePageStore();
const tenantStore = useTenantStore();
const userStore = useUserStore();

const dropdownMenu = ref<InstanceType<typeof DropdownMenu> | null>(null);

const propertyConfig = computed(() => props.params.property && getPropertyConfig(props.params.property.kind));
const isListMiniRoadmap = computed(() => props.params.editorMode === EditorMode.LIST_MINI_ROADMAP);
const isTrash = computed(() => appStore.currentPage?.kind === ViewKind.TRASH);

const notFlat = computed(() => appStore.subtaskDisplayMode !== SubtaskDisplayMode.FLAT);

const filterDefinitions = computed(() => getFilterDefinitionList(getPropertyWithConfigList()));
const fieldOrDuid = computed(() => (props.params.column.getDefinition() as { field: TaskField }).field);
const isSortEnabled = computed(() => props.params.enableSorting);
const isFilterEnabled = computed(() => filterDefinitions.value.find((e) => e.propertyDuid === fieldOrDuid.value));
const leftPaddingWidth = computed(() => {
  if (propertyConfig.value?.kind !== PropertyKind.DEFAULT_TITLE) {
    return 8;
  }
  return (
    calculateListLeftPadding(notFlat.value, pageStore.isMobile, pageStore.hasTouch) +
    (pageStore.isPublic ? (pageStore.isMobile ? 8 : -8) : pageStore.isMobile ? 8 : 16)
  );
});

const columnIcon = computed(() =>
  propertyConfig.value ? propertyConfig.value.icon : COLUMN_ICON_MAP.get(fieldOrDuid.value)
);

const isFilterActive = computed(() =>
  appStore.filters.some((filter) => filter.propertyDuid === fieldOrDuid.value && filter.values.length > 0)
);
const activeSortDirection = computed(() => sortDirection(fieldOrDuid.value));
const isSortActive = computed(() => activeSortDirection.value !== NO_SORT);

const handleFilterClick = () => {
  if (!userStore.getShowFilters()) {
    userStore.setShowFilters(true);
  }
  nextTick(() => {
    if (isFilterActive.value) {
      appStore.activeFilterArea?.openFilter(fieldOrDuid.value);
    } else {
      appStore.activeFilterArea?.addFilter(fieldOrDuid.value);
    }
  });
};

const wrapper = ref<HTMLDivElement | null>(null);
const rightContent = ref<HTMLDivElement | null>(null);
const wrapperWidth = ref(props.params.column.getMaxWidth() ?? 100);
const showIcon = ref(true);

const resize = () => {
  if (!wrapper.value) {
    return;
  }
  wrapperWidth.value = props.params.column.getMaxWidth() ?? 100;
  const rightContentWidth = rightContent.value?.getBoundingClientRect()?.width ?? 0;
  const padding = 8 + (rightContentWidth > 0 ? 4 : 0) + leftPaddingWidth.value;
  showIcon.value = wrapperWidth.value - rightContentWidth - padding >= 50;
};

useResizeObserver(rightContent, resize);
watch([() => wrapper.value, () => rightContent.value, () => leftPaddingWidth.value], resize);

const defaultValues = getRecommendationDefaultValuesForProperties();

const showRecommendationButton = computed(
  () =>
    pageStore.isOnline && userStore.isRoleGte(UserRole.MEMBER) && [...defaultValues.keys()].includes(fieldOrDuid.value)
);

const createAutomationForColumn = async () => {
  const { property } = props.params;
  const dartboardDuid = appStore.currentDartboardDuid;
  if (!property || !dartboardDuid) {
    return;
  }

  const automation = await dataStore.createAutomation(undefined, {
    title: `Set ${property.title}`,
    currentConditions: [
      {
        id: makeUuid(),
        propertyDuid: dataStore.defaultDartboardProperty.duid,
        applicability: FilterApplicability.IS,
        connector: FilterConnector.AND,
        values: [dartboardDuid],
      },
    ],
    propertyUpdates: { [property.duid]: null },
  });
  await router.push(makeLinkToAutomationSettingsRef(automation.duid).value);
};

const generateRecommendation = async () => {
  const visualization = appStore.getActiveVisualization();
  const tasks = await dataStore.getTasksByDuids(
    visualization.getAll().map((e) => e.duid),
    { includeTrashed: false, includeDraft: false }
  );

  if (tasks.length > 100) {
    notify({ message: "Dart AI cannot fill out more than 100 tasks at once", type: NotificationType.ERROR });
    return;
  }

  const taskDuids = tasks.map((e) => e.duid);

  const value = defaultValues.get(fieldOrDuid.value);

  appStore.startLoadingAiTaskProperties(taskDuids, fieldOrDuid.value);
  const [taskUpdates, recDuids] = await generateFieldRecommendation(
    tasks,
    value?.[0] ?? fieldOrDuid.value,
    value?.[1] ?? null
  );
  dataStore.updateTasks(taskUpdates);
  appStore.finishLoadingAiTaskProperties(taskDuids, fieldOrDuid.value);

  appStore.showFeedbackTooltip(wrapper.value, recDuids);
};

const dropdownSections = computed(() => {
  const result = [];
  if (isFilterEnabled.value) {
    result.push({
      title: "Filter",
      items: [
        {
          title: "Filter",
          kind: DropdownMenuItemKind.BUTTON,
          icon: FilterIcon,
          onClick: handleFilterClick,
        },
      ],
    });
  }
  if (isSortEnabled.value) {
    result.push({
      title: "Sort",
      items: [
        {
          title: `${activeSortDirection.value === ASCENDING ? "Unsort" : "Sort ascending"}`,
          kind: DropdownMenuItemKind.BUTTON,
          icon: activeSortDirection.value === ASCENDING ? UnsortAscendingIcon : SortAscendingIcon,
          onClick: () => {
            setSort(activeSortDirection.value === ASCENDING ? NO_SORT : ASCENDING, fieldOrDuid.value);
          },
        },
        {
          title: `${activeSortDirection.value === DESCENDING ? "Unsort" : "Sort descending"}`,
          kind: DropdownMenuItemKind.BUTTON,
          icon: activeSortDirection.value === DESCENDING ? UnsortDescendingIcon : SortDescendingIcon,
          onClick: () => {
            setSort(activeSortDirection.value === DESCENDING ? NO_SORT : DESCENDING, fieldOrDuid.value);
          },
        },
      ],
    });
  }
  const { property } = props.params;
  if (!property || property.kind === PropertyKind.DEFAULT_TITLE) {
    return result;
  }

  const metaItems = [];
  if (
    property.kind !== PropertyKind.DEFAULT_DARTBOARD &&
    !(isListMiniRoadmap.value && property.kind === PropertyKind.DEFAULT_DATES)
  ) {
    metaItems.push({
      title: "Hide column",
      kind: DropdownMenuItemKind.BUTTON,
      icon: HideIcon,
      hidden: isTrash.value,
      onClick: () => {
        appStore.setPropertyDuidVisibility(property.duid, false);
      },
    });
  }
  if (
    propertyConfig.value &&
    property.kind !== PropertyKind.DEFAULT_DARTBOARD &&
    !propertyConfig.value.readOnly &&
    propertyConfig.value.automation !== null
  ) {
    metaItems.push({
      title: "Create automation",
      kind: DropdownMenuItemKind.BUTTON,
      icon: AutomationsIcon,
      hidden: !userStore.isRoleGte(UserRole.ADMIN) || !appStore.currentDartboardDuid || isTrash.value,
      onClick: createAutomationForColumn,
    });
  }
  if (props.params.property && !NO_SETTINGS_PROPERTY_KIND_SET.has(props.params.property.kind)) {
    metaItems.push({
      title: "Property settings",
      kind: DropdownMenuItemKind.INTERNAL_LINK,
      icon: SettingsIcon,
      hidden: !userStore.isRoleGte(UserRole.ADMIN) || isTrash.value,
      navigate: { to: makeLinkToPropertySettingsRef(props.params.property.duid).value },
    });
  }
  result.push({
    title: "Meta",
    items: metaItems,
  });

  return result;
});

const onContextMenu = (event: MouseEvent) => {
  if ((tenantStore.isDart && !pageStore.adminHidden && event.altKey) || pageStore.isPublicView) {
    return;
  }

  // TODO pass the recommendation generation through
  appStore.openContextMenu(event as PointerEvent, dropdownSections.value);
};

onUnmounted(() => {
  appStore.showFeedbackTooltip(null, []);
});
</script>

<template>
  <DropdownMenu ref="dropdownMenu" :sections="dropdownSections" :disabled="pageStore.isPublicView" block height-block>
    <template v-if="showRecommendationButton" #footer="{ close }">
      <FillOutWithAiItem :close="close" @recommend="generateRecommendation" />
    </template>
    <div
      ref="wrapper"
      class="flex size-full items-center gap-1 pr-2"
      :class="[
        !pageStore.isMobile || propertyConfig?.kind === PropertyKind.DEFAULT_TITLE
          ? 'justify-between'
          : 'justify-center',
        isTrash && '!pointer-events-auto',
        !pageStore.isPublicView && 'cursor-pointer hover:bg-lt',
      ]"
      :style="{ 'max-width': `${wrapperWidth}px`, 'padding-left': `${leftPaddingWidth}px` }"
      @contextmenu="onContextMenu">
      <div class="flex min-w-0 shrink items-center gap-2">
        <component :is="columnIcon" v-if="showIcon && columnIcon" class="icon-sm text-md" />
        <span
          v-if="!pageStore.isMobile || propertyConfig?.kind === PropertyKind.DEFAULT_TITLE"
          :title="props.params.displayName"
          class="shrink truncate text-xs font-normal text-md select-none">
          {{ props.params.displayName }}
        </span>
      </div>
      <div
        v-if="!pageStore.isPublicView && !pageStore.isMobile && (isFilterActive || isSortActive)"
        ref="rightContent"
        class="flex shrink-0 items-center"
        data-global-popper-ignore
        @click.stop
        @keydown.enter.stop>
        <button
          v-if="isFilterActive"
          type="button"
          aria-label="Edit filter"
          class="rounded-sm text-vlt focus-ring-lt hover:text-lt"
          @click="handleFilterClick">
          <FilterIcon class="icon-sm" />
        </button>
        <SortIndicator :property-duid="fieldOrDuid" />
      </div>
    </div>
  </DropdownMenu>
</template>
